import React, {useEffect, useState} from "react";
import {
    Table,
    Card,
    Typography,
    Upload,
    Button,
    Modal,
    Form,
    Select,
    DatePicker,
    TimePicker,
    message,
    Input,
    Row,
    Col,
    Tabs,
} from "antd";
import api from "../../api";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import {useNavigate} from "react-router-dom";
import {CheckCircleTwoTone, CloseCircleTwoTone, PlusOutlined, SearchOutlined, UploadOutlined} from "@ant-design/icons";
import {exportHotelsByStatus} from "../../utils/exportHotelsByStatus";
import EditableStatusCell from "../../components/EditableStatusCell";

const {Title} = Typography;
const {Option} = Select;
const {RangePicker} = DatePicker;
dayjs.extend(isSameOrAfter);

export interface HotelRecord {
    id: number;
    status: "before_confirmation" | "confirmed" | "change_requested" | "confirmed_with_changes" | "cancelled";
    customer: {
        id: number;
        first_name: string;
        last_name: string;
        mmid: string;
    };
    checkin_date?: string;
    checkout_date?: string;
    rooms?: string;
    tower?: string;
    room_types?: string[];
    special_requests?: string[];
    early_checkin_time?: string | null;
    late_checkout_time?: string | null;
    splash_bay?: boolean | string;
    payment_type?: string;
    confirmation_number?: string;
    notes?: string;

    [key: string]: any;
}

const HotelPage: React.FC = () => {
    const [hotels, setHotels] = useState<any[]>([]);
    const [customers, setCustomers] = useState<any[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [form] = Form.useForm();
    const specialRequests = Form.useWatch("special_requests", form);
    const numRooms = Form.useWatch("rooms", form);
    const checkinDate = Form.useWatch("checkin_date", form);
    const checkoutDate = Form.useWatch("checkout_date", form);
    const [total, setTotal] = useState(0);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const [searchText, setSearchText] = useState("");
    const [checkinRange, setCheckinRange] = useState<any>(null);
    const [checkoutRange, setCheckoutRange] = useState<any>(null);
    const [sortOrder, setSortOrder] = useState<"newest" | "oldest" | null>("newest");
    const [activeStatus, setActiveStatus] = useState<string | "all">("all");
    const [nightPayments, setNightPayments] = useState<{ date: string; payment: string }[]>([]);


    const statusTabs = [
        {key: "all", label: "All"},
        {key: "before_confirmation", label: "Before Confirmation"},
        {key: "confirmed", label: "Confirmed"},
        {key: "change_requested", label: "Change Requested"},
        {key: "confirmed_with_changes", label: "Confirmed (with changes)"},
        {key: "cancelled", label: "Cancelled"},
        {key: "all_confirmed", label: "All Confirmed"},
    ];

    useEffect(() => {
        fetchHotels();
    }, []);

    useEffect(() => {
        fetchHotels(pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize, searchText, checkinRange, checkoutRange, sortOrder, activeStatus]);

    useEffect(() => {
        if (checkinDate && checkoutDate && dayjs.isDayjs(checkinDate) && dayjs.isDayjs(checkoutDate)) {
            const nights: { date: string; payment: string }[] = [];
            let current = checkinDate.startOf("day");
            const end = checkoutDate.startOf("day");

            while (current.isBefore(end)) {
                nights.push({
                    date: current.format("YYYY-MM-DD"),
                    payment: nights.length === 0 ? "IM COMP" : "",
                });
                current = current.add(1, "day");
            }
            setNightPayments(nights);
            form.setFieldsValue({
                per_night_payments: nights.reduce(
                    (acc, n) => ({...acc, [n.date]: n.payment}),
                    {}
                ),
            });
        }
    }, [checkinDate, checkoutDate]);


    const renderBooleanIcon = (value: boolean | string) => {
        if (value === true || value === "Yes") return <CheckCircleTwoTone twoToneColor="#52c41a"/>;
        if (value === false || value === "No") return <CloseCircleTwoTone twoToneColor="#ff4d4f"/>;
        return "n/a";
    };

    const fetchHotels = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const params: any = {
                page,
                page_size: pageSize,
            };

            if (searchText) {
                params.search = searchText;
            }

            if (checkinRange?.[0] && checkinRange?.[1]) {
                params.checkin_date__gte = checkinRange[0].startOf("day").format("YYYY-MM-DD");
                params.checkin_date__lte = checkinRange[1].endOf("day").format("YYYY-MM-DD");
            }

            if (checkoutRange?.[0] && checkoutRange?.[1]) {
                params.checkout_date__gte = checkoutRange[0].startOf("day").format("YYYY-MM-DD");
                params.checkout_date__lte = checkoutRange[1].endOf("day").format("YYYY-MM-DD");
            }

            if (sortOrder === "oldest") {
                params.ordering = "created_at";
            } else if (sortOrder === "newest") {
                params.ordering = "-created_at";
            } else {
                params.ordering = "closest_checkin";  // default or custom sort
            }

            if (activeStatus && activeStatus !== "all") {
                if (activeStatus === "all_confirmed") {
                    params.status__in = "confirmed,confirmed_with_changes";
                } else {
                    params.status = activeStatus;
                }
            }

            const res = await api.get("/cc/hotel/", {params});
            setHotels(res.data.results);
            setTotal(res.data.count || 0);
        } catch {
            message.error("Failed to load hotels");
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomerOptions = async (query: string) => {
        setLoading(true);
        try {
            const res = await api.get("/cc/customers/", {
                params: {
                    search: query,
                    page_size: 20,
                    ordering: "-created_at"
                }
            });
            setCustomers(res.data.results || res.data);
        } finally {
            setLoading(false);
        }
    };

    const handleExcelUpload = async (options: any) => {
        const {file, onSuccess, onError} = options;

        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await api.post("/cc/hotel-upload-excel/", formData, {
                headers: {"Content-Type": "multipart/form-data"},
            });

            message.success("Hotel reservations uploaded successfully");
            fetchHotels();
            onSuccess?.("ok");
        } catch (error: any) {
            const messageText = error?.response?.data?.error || "Upload failed.";
            const details = error?.response?.data?.details || [];

            message.error(messageText);

            if (details.length > 0) {
                Modal.error({
                    title: "Upload Failed Details",
                    content: (
                        <div>
                            {details.map((detail: string, idx: number) => (
                                <p key={idx} style={{marginBottom: 4}}>{detail}</p>
                            ))}
                        </div>
                    ),
                    width: 600,
                    onOk: () => {
                        fetchHotels();
                    },
                });
            }

            onError?.(error);
        }
    };

    const uploadProps = {
        name: "file",
        accept: ".xlsx, .xls",
        maxCount: 1,
        customRequest: handleExcelUpload,
        showUploadList: false,
    };

    const handleCreate = async (values: any) => {
        try {
            const payload = {
                ...values,
                rooms: 1,
                room_types: values.room_types ? [values.room_types] : [],
                splash_bay: false,
                payment_type: "IM COMP",
                customer_id: values.customer,
                checkin_date: values.checkin_date?.format("YYYY-MM-DD"),
                checkout_date: values.checkout_date?.format("YYYY-MM-DD"),
                early_checkin_time: values.early_checkin_time?.format("HH:mm:ss") || null,
                late_checkout_time: values.late_checkout_time?.format("HH:mm:ss") || null,
                per_night_payments: values.per_night_payments,
            };

            await api.post("/cc/hotel/", payload);
            message.success("Hotel reservation created");
            setModalOpen(false);
            form.resetFields();
            fetchHotels();
        } catch {
            message.error("Failed to create hotel entry");
        }
    };

    const columns = [
        {
            title: "Customer",
            dataIndex: ["customer", "first_name"],
            render: (_: any, rec: any) => `${rec.customer.first_name} ${rec.customer.last_name}`
        },
        {title: "Tower", dataIndex: "tower"},
        {
            title: "Room Type",
            dataIndex: "room_types",
            render: (types: string[]) => types?.join(", ") || "n/a",
        },
        {title: "Check-in", dataIndex: "checkin_date"},
        {title: "Check-out", dataIndex: "checkout_date"},
        // {
        //     title: "Splash Bay",
        //     dataIndex: "splash_bay",
        //     render: renderBooleanIcon
        // },
        {
            title: "Status",
            dataIndex: "status",
            render: (_: any, record: HotelRecord) => (
                <EditableStatusCell
                    record={record}
                    fetchHotels={fetchHotels}
                    pagination={pagination}
                />
            ),
        }
    ];

    return (
        <Card
            title={
                <Row justify="space-between" align="middle" gutter={8}>
                    <Col>
                        <Title level={3} style={{margin: 0}}>
                            Casino Club Hotel Reservations
                        </Title>
                    </Col>
                    <Col flex="auto"/>
                    <Col>
                        <Upload {...uploadProps}>
                            <Button style={{backgroundColor: "#52c41a", color: "white", borderColor: "#52c41a"}}
                                    icon={<UploadOutlined/>}>Upload Excel</Button>
                        </Upload>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            onClick={() => setModalOpen(true)}
                        >
                            New Reservation
                        </Button>
                    </Col>
                </Row>
            }
            style={{margin: "16px", padding: "16px"}}
        >
            <Row gutter={[16, 16]} style={{marginBottom: 16}} wrap>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Input
                        placeholder="Search by Name or MMID"
                        prefix={<SearchOutlined/>}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                    />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <RangePicker
                        style={{width: "100%"}}
                        placeholder={["Check-in Start", "Check-in End"]}
                        onChange={setCheckinRange}
                    />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <RangePicker
                        style={{width: "100%"}}
                        placeholder={["Checkout Start", "Checkout End"]}
                        onChange={setCheckoutRange}
                    />
                </Col>
                {/*<Col xs={24} sm={12} md={8} lg={6}>*/}
                {/*    <Select*/}
                {/*        value={sortOrder}*/}
                {/*        onChange={(value) => setSortOrder(value)}*/}
                {/*        style={{width: "100%"}}*/}
                {/*    >*/}
                {/*        <Option value="newest">Newest First</Option>*/}
                {/*        <Option value="oldest">Oldest First</Option>*/}
                {/*    </Select>*/}
                {/*</Col>*/}
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Button
                        block
                        type="primary"
                        style={{maxWidth: 200}}
                        onClick={() => {
                            exportHotelsByStatus(
                                hotels,
                                `CasinoClub_Hotel_Reservations_${dayjs().format("YYYY-MM-DD")}.xlsx`
                            );
                        }}
                    >
                        Export Excel
                    </Button>
                </Col>
            </Row>

            <Tabs
                activeKey={activeStatus}
                onChange={(key) => {
                    setActiveStatus(key);
                    setPagination({current: 1, pageSize: pagination.pageSize}); // reset to page 1
                }}
                items={statusTabs}
            />
            <Table
                rowKey="id"
                dataSource={hotels}
                loading={loading}
                columns={columns}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination({current: page, pageSize});
                    },
                }}
                scroll={{x: "max-content"}}
                onRow={(record) => {
                    const isPastCheckout = record.checkout_date && dayjs(record.checkout_date).isBefore(dayjs().startOf("day"));
                    return {
                        onClick: () => navigate(`/cc/hotel/${record.id}`),
                        style: {
                            cursor: "pointer",
                            transition: "background-color 0.3s ease",
                            backgroundColor: isPastCheckout ? "#f5f5f5" : "#ffffff"
                        }
                    };
                }}
                className="rounded-lg overflow-hidden"
            />

            <Modal
                title="New Hotel Reservation"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                centered
                width={700}
                bodyStyle={{maxHeight: "70vh", overflowY: "auto"}}
            >
                <Form layout="vertical" form={form} onFinish={handleCreate} initialValues={{rooms: 1}}>
                    <Form.Item name="customer" label="Customer Name" rules={[{required: true}]}>
                        <Select
                            showSearch
                            placeholder="Type name or MMID"
                            filterOption={false} // disable client-side filtering
                            onSearch={fetchCustomerOptions} // call API with query
                            options={customers.map(cust => ({
                                value: cust.id,
                                label: `${cust.first_name} ${cust.last_name} (${cust.mmid})`
                            }))}
                            notFoundContent={loading ? "Loading..." : null}
                        />
                    </Form.Item>

                    <Form.Item name="checkin_date" label="Check-in Date" rules={[{required: true}]}>
                        <DatePicker
                            style={{width: "100%"}}
                            disabledDate={(current) => current && current < dayjs().startOf("day")}
                        />
                    </Form.Item>

                    <Form.Item
                        name="checkout_date"
                        label="Check-out Date"
                        rules={[
                            {required: true},
                            {
                                validator: (_, value) => {
                                    if (!value || !dayjs.isDayjs(value)) return Promise.resolve();
                                    if (!checkinDate || value.isAfter(checkinDate, "day")) return Promise.resolve();
                                    return Promise.reject(new Error("Checkout date must be after check-in date"));
                                },
                            },
                        ]}
                    >
                        <DatePicker
                            style={{width: "100%"}}
                            disabledDate={(current) =>
                                current &&
                                (current < dayjs().startOf("day") ||
                                    (checkinDate && current.isBefore(checkinDate, "day")))
                            }
                        />
                    </Form.Item>
                    {nightPayments.length > 0 && (
                        <div style={{marginBottom: 16}}>
                            <Typography.Text strong>Payment Type Per Night</Typography.Text>
                            {nightPayments.map((n, idx) => (
                                <Form.Item
                                    key={n.date}
                                    label={dayjs(n.date).format("MMM D, YYYY")}
                                    name={["per_night_payments", n.date]}
                                    initialValue={n.payment}
                                    rules={[{required: true, message: "Please select a payment type"}]}
                                >
                                    <Select disabled={idx === 0}>
                                        <Select.Option value="IM Comp / Guest Pay">IM Comp / Guest Pay</Select.Option>
                                        <Select.Option value="IM Comp / Casino Club PM">IM Comp / Casino Club
                                            PM</Select.Option>
                                        <Select.Option value="Guest Pay">Guest Pay</Select.Option>
                                        <Select.Option value="Casino Club PM">Casino Club PM</Select.Option>
                                        <Select.Option value="DAIA PM">DAIA PM</Select.Option>
                                    </Select>
                                </Form.Item>
                            ))}
                        </div>
                    )}

                    {/*<Form.Item name="rooms" label="Rooms" rules={[{required: true}]}>*/}
                    {/*    <Select>*/}
                    {/*        {[1, 2, 3, 4].map((n) => (*/}
                    {/*            <Option key={n} value={n}>*/}
                    {/*                {n}*/}
                    {/*            </Option>*/}
                    {/*        ))}*/}
                    {/*    </Select>*/}
                    {/*</Form.Item>*/}

                    <Form.Item name="tower" label="Tower">
                        <Select>
                            <Option value="Sun">Sun</Option>
                            <Option value="Ocean">Ocean</Option>
                            <Option value="Forest">Forest</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="room_types" label="Room Type">
                        <Select>
                            <Option value="Deluxe Double">Deluxe Double</Option>
                            <Option value="Deluxe King">Deluxe King</Option>
                            <Option value="Studio Suite">Studio Suite</Option>
                            <Option value="Sun Suite">Sun Suite</Option>
                            <Option value="Inspire Suite">Inspire Suite</Option>
                            <Option value="Forest Suite">Forest Suite</Option>
                            <Option value="Ocean Suite">Ocean Suite</Option>
                            <Option value="Villa">Villa</Option>
                        </Select>
                    </Form.Item>

                    {/*<Form.Item name="payment_type" label="Payment Type">*/}
                    {/*    <Select>*/}
                    {/*        <Option value="IM Comp / Guest Pay">IM Comp / Guest Pay</Option>*/}
                    {/*        <Option value="IM Comp / Casino Club PM">IM / Casino Club*/}
                    {/*            PM</Option>*/}
                    {/*        <Option value="Guest Pay">Guest Pay</Option>*/}
                    {/*        <Option value="Casino Club PM">Casino Club PM</Option>*/}
                    {/*        <Option value="DAIA PM">DAIA PM</Option>*/}
                    {/*    </Select>*/}
                    {/*</Form.Item>*/}

                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={3}/>
                    </Form.Item>

                    <Form.Item name="special_requests" label="Special Requests">
                        <Select mode="multiple">
                            <Option value="Early Check-in">Early Check-in</Option>
                            <Option value="Late Checkout">Late Checkout</Option>
                            <Option value="Extra Bed">Extra Bed</Option>
                        </Select>
                    </Form.Item>

                    {specialRequests?.includes("Early Check-in") && (
                        <Form.Item name="early_checkin_time" label="Early Check-in Time">
                            <TimePicker format="HH:mm" style={{width: "100%"}}/>
                        </Form.Item>
                    )}

                    {specialRequests?.includes("Late Checkout") && (
                        <Form.Item name="late_checkout_time" label="Late Checkout Time">
                            <TimePicker format="HH:mm" style={{width: "100%"}}/>
                        </Form.Item>
                    )}

                    {/*<Form.Item name="splash_bay" label="Splash Bay">*/}
                    {/*    <Select>*/}
                    {/*        <Option value="Yes">Yes</Option>*/}
                    {/*        <Option value="No">No</Option>*/}
                    {/*    </Select>*/}
                    {/*</Form.Item>*/}
                </Form>
            </Modal>
        </Card>
    );
};

export default HotelPage;
